import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SalesDrilldownEvent,
  type SalesDrilldownKind,
  type SalesReportQuery,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { ExportCsvButton } from './ExportCsvButton';
import { cn } from '@/utils/cn';
import { useSalesRecordsQuery } from '../api/reports.api';

const PAGE_SIZE = 20;

/** An event that takes money back off the figure it belongs to is marked, never hidden. */
const REVERSING_EVENTS: ReadonlySet<string> = new Set([SalesDrilldownEvent.CREDIT_NOTE_VOIDED]);

/**
 * The records behind one headline figure.
 *
 * The server filters and pages them under the report's own scope and period, so this shows exactly
 * what the figure counted and nothing the reader could not see. A Returns drilldown is a stream of
 * credit-note events: an issue on the day it was issued, and a void as its own row on the day of
 * the void, signed so the rows of a period add up to the Returns figure.
 */
export function SalesRecords({
  query,
  kind,
  onClose,
}: {
  query: SalesReportQuery;
  kind: SalesDrilldownKind;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [page, setPage] = useState(1);
  const recordsQuery = { ...query, kind, page, pageSize: PAGE_SIZE };
  const records = useSalesRecordsQuery(recordsQuery);
  const pages = records.data ? Math.max(1, Math.ceil(records.data.total / PAGE_SIZE)) : 1;
  const headingId = `sales-records-${kind}`;

  return (
    <Card className="p-4">
      <section aria-labelledby={headingId}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 id={headingId} className="text-ink font-semibold">
            {t(`reports.sales.records.titles.${kind}`)}
          </h3>
          <span className="flex flex-wrap items-baseline gap-3">
            <ExportCsvButton
              target={{ method: 'GET', path: '/reports/sales/records/export', query: recordsQuery }}
            />
            <button
              type="button"
              onClick={onClose}
              className="text-brand-700 text-sm font-medium hover:underline"
            >
              {t('reports.sales.records.close')}
            </button>
          </span>
        </div>
        {kind === 'RETURNS' ? (
          <p className="text-muted mt-1 text-xs">{t('reports.sales.records.returnsNote')}</p>
        ) : null}

        {records.isError ? (
          <div className="mt-3">
            <Alert tone="error">{t('reports.sales.records.loadError')}</Alert>
          </div>
        ) : null}
        {records.isPending ? (
          <div
            className="bg-surface border-line mt-3 h-24 animate-pulse rounded-lg border"
            aria-hidden
          />
        ) : null}
        {records.data && records.data.rows.length === 0 ? (
          <p className="text-muted mt-3 text-sm">{t('reports.sales.records.empty')}</p>
        ) : null}

        {records.data && records.data.rows.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="text-muted border-line border-b">
                  <th className="py-2 text-start font-medium">
                    {t('reports.sales.records.columns.event')}
                  </th>
                  <th className="py-2 text-start font-medium">
                    {t('reports.sales.records.columns.reference')}
                  </th>
                  <th className="py-2 text-start font-medium">
                    {t('reports.sales.records.columns.date')}
                  </th>
                  <th className="py-2 text-start font-medium">
                    {t('reports.sales.records.columns.branch')}
                  </th>
                  <th className="py-2 text-start font-medium">
                    {t('reports.sales.records.columns.operator')}
                  </th>
                  <th className="py-2 text-end font-medium">
                    {t('reports.sales.records.columns.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.data.rows.map((row) => (
                  <tr
                    key={`${row.event}-${row.id}`}
                    className="border-line border-b last:border-b-0"
                    data-event={row.event}
                  >
                    <td className="py-2">
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-xs font-medium',
                          REVERSING_EVENTS.has(row.event)
                            ? 'bg-amber-50 text-amber-800'
                            : 'bg-brand-50 text-brand-700',
                        )}
                      >
                        {t(`reports.sales.records.events.${row.event}`)}
                      </span>
                    </td>
                    <td className="py-2" dir="ltr">
                      {row.reference}
                    </td>
                    <td className="py-2 tabular-nums" dir="ltr">
                      {row.businessDate}
                    </td>
                    <td className="py-2">{row.branchName}</td>
                    <td className="py-2">
                      {row.operatorName ?? (
                        <span className="text-muted italic">
                          {t('reports.sales.records.unknownOperator')}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-end tabular-nums" dir="ltr">
                      {formatMoney(row.amountMinor, locale, row.currencyCode)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {records.data && pages > 1 ? (
          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.previous')}
            </button>
            <span className="text-muted">{t('reports.sales.records.page', { page, pages })}</span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setPage((current) => current + 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.next')}
            </button>
          </div>
        ) : null}
      </section>
    </Card>
  );
}
